// Token login against the Medzcool API's /auth/ endpoints.
//
// Kardiograph has no login form of its own: users sign in on medzcool.com,
// which sends them here with a one-time `?code=` (see exchangeCodeFromUrl).
// The code becomes a token pair (15 minute access, 30 day refresh) kept in
// localStorage. Every API call carries `Authorization: Bearer`; a 401 means
// the access token expired, so we refresh once and retry.
import { url } from "./settings";

const ACCESS_KEY = "medzcool.access";
const REFRESH_KEY = "medzcool.refresh";

export function getAccessToken() {
  try {
    return localStorage.getItem(ACCESS_KEY);
  } catch (e) {
    return null;
  }
}

export function getRefreshToken() {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch (e) {
    return null;
  }
}

export function setTokens({ access, refresh }) {
  try {
    if (access) localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  } catch (e) {
    // Private mode or storage disabled: the session cookie still works.
  }
}

export function clearTokens() {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch (e) {}
}

export function authHeaders(headers = {}) {
  const access = getAccessToken();
  return access ? { ...headers, Authorization: `Bearer ${access}` } : headers;
}

// One refresh at a time: concurrent 401s share the same promise.
let refreshing = null;

export function refreshTokens() {
  if (refreshing) return refreshing;
  const refresh = getRefreshToken();
  if (!refresh) return Promise.resolve(false);
  refreshing = fetch(`${url}/auth/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  })
    .then(async (response) => {
      if (!response.ok) {
        clearTokens();
        return false;
      }
      setTokens(await response.json());
      return true;
    })
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

// `fetch` with the Bearer header, retried once after a refresh on 401.
export async function fetchWithAuth(input, init = {}) {
  const send = () =>
    fetch(input, { ...init, headers: authHeaders(init.headers || {}) });
  let response = await send();
  if (response.status === 401 && getRefreshToken()) {
    if (await refreshTokens()) response = await send();
  }
  return response;
}

// On arrival from medzcool.com/login/kardiograph the URL carries a one-time
// code. Exchange it for this app's own token pair and drop it from the URL.
// Resolves when done (true if tokens were stored), so the app can wait
// before its first query.
export async function exchangeCodeFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  if (!code) return false;
  params.delete("code");
  const query = params.toString();
  window.history.replaceState(
    null,
    "",
    window.location.pathname + (query ? `?${query}` : "") + window.location.hash
  );
  try {
    const response = await fetch(`${url}/auth/exchange/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
    if (!response.ok) return false;
    setTokens(await response.json());
    return true;
  } catch (e) {
    return false;
  }
}

export async function logoutTokens() {
  const refresh = getRefreshToken();
  clearTokens();
  if (refresh) {
    try {
      await fetch(`${url}/auth/logout/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
    } catch (e) {}
  }
}

// Where to send a visitor who is not signed in: medzcool.com signs them in
// (or finds them already signed in) and hands them back with a code.
export const LOGIN_URL = "https://medzcool.com/login/kardiograph";
